import { Avatar, Button, Spinner, Tooltip } from 'flowbite-react';
import Lottie from 'react-lottie-player';
import arrowAnimation from './arrowAnimation.json';
import { useCallback, useEffect, useState } from 'react';
import { ApplicationStatus, BaseAPIResponse, CreateApplicationInput, UIExtendedApplication } from 'types';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { Img } from 'react-image';
import { ApplicationDrawerDetail } from 'src/pages/MyApplications';
import { UseMutationResult } from 'react-query';
import moment from 'moment';

interface Props {
	isFooter?: boolean;
	isLoadingApplying: boolean;
	showCta: boolean;
	myApplicationStatus: ApplicationStatus;
	setIsApplicationDrawerOpen: (arg: boolean) => void;
	setIsLinkedInResumeModalOpen: (arg: boolean) => void;
	company: ApplicationDrawerDetail;
	createApplicationMutation: UseMutationResult<BaseAPIResponse, Error, CreateApplicationInput>;
	setCurrentlySelectedApplication: (arg: UIExtendedApplication) => void;
	application: UIExtendedApplication;
	hasBond?: boolean;
}

const ApplicationDrawerHeaderFooter = ({
	company,
	isFooter = false,
	isLoadingApplying,
	showCta,
	myApplicationStatus,
	setIsApplicationDrawerOpen,
	setIsLinkedInResumeModalOpen,
	createApplicationMutation,
	setCurrentlySelectedApplication,
	application,
	hasBond,
}: Props) => {
	const showCompanyAvatar = !isFooter;
	const showLottie = !isFooter;

	// Use application.status as the source of truth, fallback to myApplicationStatus prop
	// This ensures the button state is always correct even if the prop is not updated
	// For matched positions (isPosition = true), there might not be an application yet, so allow applying
	const actualStatus = application?.status || myApplicationStatus;
	const ctaText = actualStatus === ApplicationStatus.APPLIED_BY_JOBSEEKER ? 'Applied' : 'Apply';

	// Check if expiry date has passed
	const isExpired = application?.expiryDate && moment(application.expiryDate).isBefore(moment());

	// Disable button if:
	// 1. Status is APPLIED_BY_JOBSEEKER (user already applied), OR
	// 2. Expiry date has passed
	// If application is null/undefined or isPosition is true, allow applying (matched positions) unless expired
	const disableCta = (!application?.isPosition && actualStatus === ApplicationStatus.APPLIED_BY_JOBSEEKER) || isExpired;

	// Hide CTA section entirely if expired (like image 3)
	const shouldShowCta = showCta && !isExpired;

	// const { isOpen: isApplyModalOpen, onOpen: onApplyModalOpen, onClose: onApplyModalClose } = useDisclosure();
	// const { isOpen: isLinkedInModalOpen, onOpen: onOpenLinkedInModal, onClose: onCloseLinkedInModal } = useDisclosure();

	const [avatarImg, setAvatarImg] = useState<string>('');

	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(company.companyLogo, company.companyUrl);
		setAvatarImg(res);
	}, [company.companyLogo, company.companyUrl]);

	const handleApply = () => {
		// if (!user?.profile?.linkedInProfile) onOpenLinkedInModal();
		// else onApplyModalOpen();
		const userLinkedInProfile = application.user?.profile.linkedInProfile;
		const userResumeLink = application.user?.profile.resumeLink;

		if (userLinkedInProfile && userResumeLink) {
			createApplicationMutation.mutate({
				createInfo: {
					position: application.position?._id.toString() || '',
					user: application.user?._id || '',
				},
			});
		} else {
			setCurrentlySelectedApplication(application);
			setIsLinkedInResumeModalOpen(true);
		}
	};

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [company.companyLogo, company.companyUrl]);

	return (
		<>
			<div className="flex justify-center dark:text-white">
				<div className="flex w-full justify-between">
					<div className="flex gap-4">
						{showCompanyAvatar ? (
							<div className="flex gap-4">
								<div>
									<Avatar
										className="drawer-avatar-container"
										size={'md'}
										alt="companyLogo"
										img={avatarImg}
										rounded={true}
										bordered
									/>
								</div>
								<div>
									<div className="text-xl font-bold">
										{company.companyPosition.length > 20 ? (
											<Tooltip content={company.companyPosition} placement="top">
												{company.companyPosition.length > 20
													? `${company.companyPosition.substring(0, 19)} ...`
													: `${company.companyPosition}`}
											</Tooltip>
										) : (
											<>
												{company.companyPosition.length > 20
													? `${company.companyPosition.substring(0, 19)} ...`
													: `${company.companyPosition}`}
											</>
										)}
									</div>
									<div
										onClick={() => window.open(`${company.companyUrl}`, '_blank')}
										className="mt-[4px] flex cursor-pointer items-center gap-2 text-base font-medium"
									>
										<span className="hover:underline">
											{company.companyName.length > 20 ? (
												<Tooltip content={company.companyName} placement="top">
													{company.companyName.length > 20
														? `${company.companyName.substring(0, 19)} ...`
														: `${company.companyName}`}
												</Tooltip>
											) : (
												<>
													{company.companyName.length > 20
														? `${company.companyName.substring(0, 19)} ...`
														: `${company.companyName}`}
												</>
											)}
										</span>
										<span>
											<Img
												loading="lazy"
												className=""
												src="/assets/externalLinkIcon.svg"
												alt="externalLinkIcon"
												width="16"
												height="16"
											/>
										</span>
									</div>

									{hasBond ? (
										<div className="mt-2 bg-[#FFF8E5] rounded-[6px] px-2 py-1 hidden md:block max-h-[25px] max-w-max text-[#FACA15] text-xs font-[500] font-inter dark:bg-[#374151] dark:border dark:border-[#FACA15]">
											*This job includes bond agreement.
										</div>
									) : null}
								</div>
							</div>
						) : null}
					</div>

					{shouldShowCta ? (
						<div className={`${isFooter ? 'grow justify-between' : 'sm:hidden'} flex items-center sm:w-full`}>
							{showLottie ? <Lottie loop animationData={arrowAnimation} play={true} className="w-[73px]" /> : null}

							{actualStatus === ApplicationStatus.APPLIED_BY_JOBSEEKER ? (
								<div className={`${showLottie ? 'ml-3 sm:ml-0' : 'ml-0'}`}>
									<div className="text-base font-medium">{`🎉 Congratulations on applying.`}</div>
									<div className="mt-[4px] text-xs font-normal text-[#00B7C4] dark:text-[#73DFE7]">
										Stay tuned for updates on the next steps.
									</div>
								</div>
							) : (
								<div
									className={`${showLottie ? 'ml-3 sm:ml-0' : 'ml-0'} text-base font-medium`}
								>{`🎉 Hurray! You're eligible to apply.`}</div>
							)}

							<div className="ml-[26px] sm:ml-0">
								<Button color="purplePlaced" size="md" onClick={handleApply} disabled={isLoadingApplying || disableCta}>
									{isLoadingApplying ? <Spinner /> : `${ctaText}`}
								</Button>
							</div>
						</div>
					) : null}
				</div>
			</div>
			{/* 
			<ApplyConfirmationModal
				isOpen={isApplyModalOpen}
				onClose={onApplyModalClose}
				applicationId={company.id}
				setIsApplicationDrawerOpen={setIsApplicationDrawerOpen}
			/> */}
			{/* <LinkedInImport
				isOpen={isLinkedInModalOpen}
				onClose={onCloseLinkedInModal}
				onProceed={() => {
					onCloseLinkedInModal();
					onApplyModalOpen();
				}}
			/> */}
		</>
	);
};

export default ApplicationDrawerHeaderFooter;
